import React, { useState, useEffect, useMemo } from 'react';
import { Theme, Typography } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { createHoursString } from '../../components/CadenceSettings/Scheduler';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { Business } from '../../../lib/kixie-entities/tables/Business';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Button from '@mui/material/Button';
import OnboardingSkeleton from './OnboardingSkeleton';
import { useDispatch, useSelector } from 'react-redux';
import {
  onResetOnboarding,
  updateOnboardingSubmit,
} from '../../store/onboardingSlice';
import CircularProgress from '@mui/material/CircularProgress';
import { green } from '@mui/material/colors';
import CheckIcon from '@mui/icons-material/Check';

import clsx from 'clsx';
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
    },
    wrapper: {
      margin: theme.spacing(1),
      position: 'relative',
    },
    buttonSuccess: {
      backgroundColor: green[500],
      '&:hover': {
        backgroundColor: green[700],
      },
    },
    buttonProgress: {
      color: 'white',
    },
  })
);
function Confirmation({
  loading,
  onboardingClasses,
  owWizardDataParsed,
  userAtom,
  handleStep,
  activeStep,
  mutation,
  enqueueSnackbarWithStyles,
  isProcessingCompleteOnboarding,
  setIsProcessingCompleteOnboarding,
  setIsCompleteOnboardingModalVisible,
  setHasClosedCompleteOnboardingModal,
  setCompleteOnboardingRejectedReason,
  refetchOnboarding,
  submittedCount,
  setOnboarding,
  setCachedOnboarding,
  setIsOnboardingLoading,
}) {
  const [hoursString, setHoursString] = useState('');

  const splitHours = hoursString.split(',');
  const classes = useStyles();
  const dispatch = useDispatch();
  const onboardingSelector = useSelector((state) => state.onboarding);
  const buttonClassname = clsx({
    [classes.buttonSuccess]: onboardingSelector?.status === 'complete',
  });
  const [
    _businessCached,
    businessLoadable,
    refetchBusiness,
  ] = makeApiRequestCached<Business>(
    {
      endpoint: `${endpointEnum.GET_BUSINESSES}/${userAtom.businessid}`,
      method: 'get',
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.[0] ?? cached.current
  );

  useEffect(() => {
    refetchBusiness({});
  }, []);

  const business = useMemo(
    () =>
      businessLoadable?.state === 'hasValue'
        ? businessLoadable?.contents?.response?.data?.[0]
        : undefined,
    [businessLoadable]
  );

  const hours = useMemo(
    () =>
      business?.businessDetails?.businessHours
        ? createHoursString(business?.businessDetails?.businessHours)
        : '',
    [business]
  );

  useEffect(() => {
    hoursString !== hours && setHoursString(hours);
  }, [hours]);

  useEffect(() => {
    if (submittedCount < 1) return;

    const submit = async () => {
      return await handleSubmit();
    };

    submit();
  }, [submittedCount]);

  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleSubmit = async () => {
    dispatch(updateOnboardingSubmit(true));
    if (
      onboardingSelector?.status === 'complete' ||
      hasSubmitted ||
      isProcessingCompleteOnboarding
    ) {
      setIsCompleteOnboardingModalVisible(true);
      return;
    }
    if (isProcessingCompleteOnboarding) {
      return;
    }
    const updateDisplayOnboarding = () =>
      mutation.mutateAsync({
        endpoint: endpointEnum.UPDATE_OW_DISPLAY,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        method: 'put',
        stage: 'itn',
        payload: {
          apiKey: userAtom?.apikey,
          businessId: userAtom?.businessid,
          displayOnboarding: false,
        },
      });

    const completeOnboarding = () =>
      mutation.mutateAsync({
        endpoint: endpointEnum.COMPLETE_OW,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        method: 'put',
        stage: 'itn',
        payload: {
          apiKey: userAtom?.apikey,
          businessId: userAtom?.businessid,
          userId: userAtom?.userid,
        },
      });

    try {
      setHasSubmitted(true);
      setOnboarding(undefined);
      setCachedOnboarding(undefined);
      setIsOnboardingLoading(true);
      setHasClosedCompleteOnboardingModal(false);
      setCompleteOnboardingRejectedReason(undefined);
      setIsProcessingCompleteOnboarding(true);
      setIsCompleteOnboardingModalVisible(true);

      const [displayOnboarding, onboarding] = await Promise.allSettled<any>([
        updateDisplayOnboarding(),
        completeOnboarding(),
      ]);

      refetchOnboarding();

      const isSuccessful = (result) =>
        result.status === 'fulfilled' ||
        (result.status === 'rejected' &&
          result.reason.response?.status === 304);

      if (onboarding.status === 'rejected') {
        const response = onboarding.reason.response;

        const label =
          response?.status === 400
            ? 'Bad request'
            : response?.status === 404
            ? 'Not found'
            : response?.status === 500
            ? 'Server error'
            : '';

        const message =
          response?.data?.errorMessage ?? onboarding.reason.message;

        setCompleteOnboardingRejectedReason(
          `${label}${message.length ? ': ' : ''}${message}`
        );
      }

      if (!isSuccessful(displayOnboarding) || !isSuccessful(onboarding)) {
        setHasSubmitted(false);

        setIsProcessingCompleteOnboarding(false);
        dispatch(onResetOnboarding);
        enqueueSnackbarWithStyles({
          message: 'Encountered an error completing Onboarding Wizard',
          alertProps: { severity: 'error' },
        });
      }
    } catch (err) {
      setHasSubmitted(false);

      setIsProcessingCompleteOnboarding(false);
      dispatch(onResetOnboarding);
      enqueueSnackbarWithStyles({
        message: 'Encountered an error completing Onboarding Wizard',
        alertProps: { severity: 'error' },
      });
    }
  };

  return loading ? (
    <OnboardingSkeleton />
  ) : (
    <div style={{ padding: '24px' }}>
      <Typography
        className={onboardingClasses.onboardingSubtitle}
        variant={'h6'}
      >
        OVERVIEW
      </Typography>
      <div style={{ fontWeight: 600, marginBottom: '16px' }}>
        Please review your settings below.
      </div>

      <div className={onboardingClasses.confirmationRow}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
          <Typography
            className={onboardingClasses.confirmationSubtitle}
            variant={'subtitle1'}
          >
            Business Name
          </Typography>
          <div style={{ fontWeight: 600 }}>{business?.name ?? ''}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
          <Typography
            className={onboardingClasses.confirmationSubtitle}
            variant={'subtitle1'}
          >
            Business Hours
          </Typography>
          <div style={{ fontWeight: 600 }}>
            {splitHours.map((string, index) => (
              <div key={index}>
                {string} <br />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={onboardingClasses.confirmationRow}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
          <Typography
            className={onboardingClasses.confirmationSubtitle}
            variant={'subtitle1'}
          >
            Location
          </Typography>
          <div style={{ fontWeight: 600 }}>
            <div>
              {owWizardDataParsed?.business?.location?.city ?? ''}
              {', '}
              {owWizardDataParsed?.business?.location?.state ?? ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
          <Typography
            className={onboardingClasses.confirmationSubtitle}
            variant={'subtitle1'}
          >
            Number of Users
          </Typography>
          <div style={{ fontWeight: 600 }}>
            {owWizardDataParsed?.business?.teamSize ?? ''}
          </div>
        </div>
      </div>
      <div className={onboardingClasses.confirmationRow}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
          <Typography
            className={onboardingClasses.confirmationSubtitle}
            variant={'subtitle1'}
          >
            CRM
          </Typography>
          <div style={{ fontWeight: 600 }}>
            {owWizardDataParsed?.business?.crm ?? ''}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', width: '50%' }}>
          <Typography
            className={onboardingClasses.confirmationSubtitle}
            variant={'subtitle1'}
          >
            Call Reporting Settings
          </Typography>
          <div style={{ fontWeight: 600 }}>
            {business?.onlyManagersCanViewCallReporting === 1 ? (
              <div>Only managers can view call reporting</div>
            ) : (
              <div>Agents can view call reporting</div>
            )}
          </div>
        </div>
      </div>
      <div className={onboardingClasses.confirmationRow}>
        {owWizardDataParsed?.teams?.map((team, i) => {
          const result = team?.name?.replace(/([A-Z])/g, ' $1');
          const teamName = result?.charAt(0)?.toUpperCase() + result?.slice(1);
          const agents = owWizardDataParsed?.agents?.filter(
            (agent) => team?.name && agent?.teams?.includes(team?.name)
          );
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '50%',
              }}
            >
              <Typography
                className={onboardingClasses.confirmationSubtitle}
                variant={'subtitle1'}
              >
                {teamName}
              </Typography>
              {agents?.length ? (
                <div style={{ fontWeight: 600 }}>
                  {agents.map((agent) => (
                    <div
                      key={`agent-${agent.email}`}
                      style={{ display: 'flex', flexDirection: 'row' }}
                    >
                      {agent.firstName} {agent.lastName}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontWeight: 600 }}>None</div>
              )}
            </div>
          );
        })}
        {owWizardDataParsed?.agents?.filter((agent) =>
          agent?.teams?.includes('No Team')
        ).length ? (
          <div
            key={9999}
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%',
            }}
          >
            <Typography
              className={onboardingClasses.confirmationSubtitle}
              variant={'subtitle1'}
            >
              No Team
            </Typography>
            <div style={{ fontWeight: 600 }}>
              {owWizardDataParsed?.agents
                ?.filter((agent) => agent?.teams?.includes('No Team'))
                .map((agent) => (
                  <div
                    key={`agent-${agent.email}`}
                    style={{ display: 'flex', flexDirection: 'row' }}
                  >
                    {agent.firstName} {agent.lastName}
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      <div
        style={{
          padding: '24px 0px 0px',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Button
          disabled={
            isProcessingCompleteOnboarding ||
            hasSubmitted ||
            onboardingSelector?.status === 'complete'
          }
          onClick={() => handleStep(activeStep - 1)}
          className={onboardingClasses.button}
        >
          Back
        </Button>
        <div className={classes.root}>
          <div className={classes.wrapper}>
            <Button
              variant="contained"
              color="primary"
              className={clsx(onboardingClasses.button, buttonClassname)}
              onClick={handleSubmit}
              endIcon={
                onboardingSelector?.status === 'complete' ? (
                  <CheckIcon />
                ) : isProcessingCompleteOnboarding ? (
                  <CircularProgress
                    className={classes.buttonProgress}
                    size={24}
                  />
                ) : (
                  <ArrowForwardIcon />
                )
              }
            >
              {onboardingSelector?.status === 'complete'
                ? 'Success'
                : isProcessingCompleteOnboarding
                ? 'Processing'
                : 'Submit'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Confirmation;
